import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import { Lines } from "@/components/marketing/Lines";
import {
  BODY,
  BODY_SMALL,
  HEADING_2,
  HEADING_2_ON_H3,
  HEADING_3,
} from "@/components/marketing/marketingType";
import {
  MID,
  NOT_WIDE,
  TINY,
  WIDE,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Laid } from "@/content/landing/sectionLayout";
import type { LandingCta } from "@/content/landing/types";
import {
  LandingButton,
  LandingImage,
  LandingSection,
} from "../LandingPrimitives";
import { sectionPad } from "./landingRun";
import { TrustFrame } from "./MiscBlocks";
import { UseCaseCard } from "./UseCaseBlocks";

/** The five stars every review on these pages is drawn under. Site chrome. */
const STARS = "/marketing/campaign/trust-stars.svg";

/** `.pp-trust-bg_visual`: the light behind the dark quote band. Site chrome. */
const BAND_LIGHT = "/marketing/campaign/quote-band.avif";
const BAND_LIGHT_SET = [
  "/marketing/campaign/quote-band-p-500.avif 500w",
  "/marketing/campaign/quote-band-p-800.avif 800w",
  "/marketing/campaign/quote-band.avif 1328w",
].join(", ");

/** `.pp-small-title`: a card's title, drawn heavier with a stroke. */
const SMALL_TITLE = {
  color: "inherit",
  fontSize: 19,
  lineHeight: "28.5px",
  WebkitTextStroke: "0.5px",
  [MID]: { fontSize: 17, lineHeight: "25.5px" },
} satisfies SxProps<Theme>;

/** `.body_new.is-13-px`: the reviewer's source, under the name. */
const SOURCE_LINE = {
  fontSize: 12.96,
  lineHeight: "18.144px",
  textAlign: "left",
} satisfies SxProps<Theme>;

/** The line under a button: bare, or `.main_header_text_btn_holder`'s quieter 14. */
function noteStyle(onDark?: boolean, quiet?: boolean): SxProps<Theme> {
  if (quiet) {
    return {
      fontSize: 14,
      lineHeight: "21px",
      color: "rgba(79, 92, 101, 0.8)",
    };
  }
  return {
    fontSize: 16,
    lineHeight: "24px",
    color: onDark ? "common.white" : marketingPalette.ink,
  };
}

/** The buttons a hero, a panel or a gallery closes on, and the line under them. */
export function Closer({
  ctas,
  notes,
  onDark,
  quiet,
}: {
  ctas?: ReadonlyArray<LandingCta>;
  notes?: ReadonlyArray<string>;
  onDark?: boolean;
  /** The line is `.main_header_text_btn_holder`. */
  quiet?: boolean;
}) {
  if (!ctas?.length && !notes?.length) return null;
  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "8px",
        width: 1,
      }}
    >
      {ctas?.length ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: "16px",
            width: 1,
            "& > a": { flex: "0 1 auto", width: "224px" },
            [NOT_WIDE]: { "& > a": { flex: "none" } },
            [MID]: {
              flexDirection: "column",
              alignItems: "stretch",
              "& > a": { width: "100%" },
            },
            [TINY]: { gap: "12px" },
          }}
        >
          {ctas.map((cta, index) => (
            <LandingButton key={index} cta={cta} variant="primary" />
          ))}
        </Box>
      ) : null}
      {notes?.map((note) => (
        <Box key={note} sx={noteStyle(onDark, quiet)}>
          {note}
        </Box>
      ))}
    </Box>
  );
}

type Quote = Laid<"customerQuote">;

/**
 * `.pp-trust_wrap`: the review on the dark band, centred under its stars, the
 * light behind it, and the reviewer under the review.
 */
function QuoteBand({ block }: { block: Quote }) {
  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "40px",
        p: "40px",
        borderRadius: "32px",
        backgroundColor: marketingPalette.darkGround,
        textAlign: "center",
        [NOT_WIDE]: { px: "24px" },
        [MID]: { gap: "32px" },
        [TINY]: { p: "24px 16px" },
      }}
    >
      <Box component="img" src={STARS} alt="" sx={{ display: "block" }} />
      <Box
        component="img"
        src={BAND_LIGHT}
        srcSet={BAND_LIGHT_SET}
        sizes="100vw"
        alt=""
        loading="lazy"
        sx={{
          position: "absolute",
          inset: 0,
          width: 1,
          height: 1,
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          maxWidth: "700px",
          color: "common.white",
          [NOT_WIDE]: { maxWidth: "none" },
        }}
      >
        <Typography component="h3" sx={[HEADING_3, { color: "inherit" }]}>
          <Lines text={block.title} />
        </Typography>
        {block.body ? (
          <Typography sx={[BODY, { color: "inherit" }]}>
            {block.body}
          </Typography>
        ) : null}
      </Box>
      <Box
        sx={{
          position: "relative",
          maxWidth: "700px",
          color: "common.white",
          [NOT_WIDE]: { maxWidth: "none" },
        }}
      >
        <Typography sx={[BODY, { color: "inherit", fontWeight: 500 }]}>
          {block.name}
        </Typography>
        {block.source ? (
          <Typography sx={[SOURCE_LINE, { opacity: 0.7 }]}>
            {block.source}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

/**
 * `.trust-ppc_wrap`: the review on a light card, the reviewer's picture and
 * name over a rule, then the stars, the headline and the review.
 */
function QuoteCard({ block }: { block: Quote }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        maxWidth: "992px",
        mx: "auto",
        p: "32px",
        borderRadius: "16px",
        backgroundColor: "rgba(26, 179, 148, 0.06)",
        [MID]: { maxWidth: "none", p: "24px" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
        {block.avatar ? (
          <LandingImage
            src={block.avatar}
            sx={{ width: "64px", height: "64px" }}
          />
        ) : null}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Box sx={[SMALL_TITLE, { color: marketingPalette.ink }]}>
            {block.name}
          </Box>
          {block.source ? (
            <Box sx={{ fontSize: 16, lineHeight: "24px" }}>{block.source}</Box>
          ) : null}
        </Box>
      </Box>
      <Box
        sx={{
          width: 1,
          height: "0.96px",
          backgroundColor: marketingPalette.outline,
        }}
      />
      <Box
        component="img"
        src={STARS}
        alt=""
        loading="lazy"
        sx={{ display: "block", width: "130px" }}
      />
      <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <Typography component="h3" sx={HEADING_3}>
          <Lines text={block.title} />
        </Typography>
        {block.body ? <Typography sx={BODY}>{block.body}</Typography> : null}
      </Box>
    </Box>
  );
}

/** One customer's review: on the dark band, or on the light card where the page sets it so. */
export function CustomerQuoteBlock({ block }: { block: Quote }) {
  return (
    <LandingSection padding={block.pad ? sectionPad(block.pad) : "top"}>
      {block.quoteCard ? (
        <QuoteCard block={block} />
      ) : (
        <QuoteBand block={block} />
      )}
    </LandingSection>
  );
}

type Objections = Laid<"objections">;

/** `.ppc-gues-item-*`: the three tones the doubts are published in, in order. */
const DOUBT_TONES = ["#009B22", "#6C14BA", "#0B6ADA"] as const;

/**
 * `.ppc-gues_wrap`: the doubts a visitor has, each framed in its tone over
 * its answer filled in the same, three across, two below the wide step.
 */
export function ObjectionsBlock({ block }: { block: Objections }) {
  return (
    <LandingSection padding={block.pad ? sectionPad(block.pad) : "both"}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "48px",
          [MID]: { gap: "32px" },
        }}
      >
        <Typography
          component="h3"
          sx={[HEADING_2_ON_H3, { textAlign: "center" }]}
        >
          <Lines text={block.title} />
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: "20px",
            width: 1,
            [NOT_WIDE]: { gridTemplateColumns: "1fr 1fr" },
            [MID]: { gridTemplateColumns: "1fr", gap: "24px" },
          }}
        >
          {block.items.map((item, index) => {
            const tone = DOUBT_TONES[index % DOUBT_TONES.length];
            return (
              <Box
                key={item.question}
                sx={{ display: "flex", flexDirection: "column", gap: "6px" }}
              >
                <Box
                  sx={{
                    p: "24px 32px",
                    border: `4px solid ${tone}`,
                    borderRadius: "16px",
                    color: marketingPalette.ink,
                    fontSize: 20,
                    lineHeight: "30px",
                    textAlign: "center",
                    WebkitTextStroke: "0.6px",
                    [NOT_WIDE]: { p: "16px", fontSize: 18, lineHeight: "27px" },
                  }}
                >
                  {item.question}
                </Box>
                <Box
                  sx={{
                    p: "32px",
                    borderRadius: "16px",
                    backgroundColor: tone,
                    color: "common.white",
                    fontSize: 22,
                    lineHeight: "33px",
                    [NOT_WIDE]: {
                      height: 1,
                      p: "24px",
                      fontSize: 20,
                      lineHeight: "30px",
                    },
                    [MID]: { fontSize: 18, lineHeight: "27px" },
                  }}
                >
                  {item.answer}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </LandingSection>
  );
}

type ReviewGrid = Laid<"reviewGrid">;

/**
 * `.testimonial_item-trust-pilot`s in the green band: each review under its
 * stars, its opening line heavier, the reviewer at the foot.
 */
export function ReviewGridBlock({ block }: { block: ReviewGrid }) {
  return (
    <LandingSection padding={block.pad ? sectionPad(block.pad) : "break"}>
      <TrustFrame title={block.title}>
        <Box
          sx={{
            position: "relative",
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(min(100%, 19rem), 1fr))",
            gap: "24px",
          }}
        >
          {block.items.map((item) => (
            <Box
              key={item.name}
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: "32px",
                p: "24px",
                borderRadius: "16px",
                backgroundColor: "common.white",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: "16px",
                }}
              >
                <Box
                  component="img"
                  src={STARS}
                  alt=""
                  loading="lazy"
                  sx={{ display: "block", mb: "16px" }}
                />
                <Box
                  sx={{
                    color: marketingPalette.ink,
                    fontSize: 18,
                    fontWeight: 500,
                    lineHeight: 1.2,
                    letterSpacing: "-0.03em",
                  }}
                >
                  {item.title}
                </Box>
                {item.body ? (
                  <Typography sx={BODY_SMALL}>{item.body}</Typography>
                ) : null}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Typography sx={[BODY, { fontWeight: 500 }]}>
                  {item.name}
                </Typography>
                {item.source ? (
                  <Typography sx={BODY_SMALL}>{item.source}</Typography>
                ) : null}
              </Box>
            </Box>
          ))}
        </Box>
      </TrustFrame>
    </LandingSection>
  );
}

type IssuePanels = Laid<"issuePanels">;
type IssuePanel = IssuePanels["panels"][number];

/** `.ppx-problem_wrap` and `.ppx-solution_wrap`: their grounds and their cards'. */
const ISSUE_TONES = {
  problem: {
    panel: { backgroundColor: "rgba(218, 36, 36, 0.07)" },
    card: {
      backgroundColor: "rgba(218, 36, 36, 0.08)",
      color: marketingPalette.ink,
    },
    label: marketingPalette.danger,
    ink: marketingPalette.ink,
  },
  solution: {
    panel: {
      backgroundImage: "linear-gradient(110deg, #000307, #000A14)",
      overflow: "hidden",
    },
    card: {
      backgroundColor: "rgba(255, 255, 255, 0.08)",
      color: "common.white",
    },
    label: "#26E193",
    ink: "common.white",
  },
} as const;

/**
 * `.body_new.is-white-small`: a dark card's line, 15 at every step, on the
 * paragraph's own 150% (140% on a phone), not the small ramp's 155%.
 */
const DARK_CARD_BODY = {
  color: "inherit",
  opacity: 0.6,
  fontSize: 15,
  lineHeight: 1.5,
  textAlign: "left",
  [TINY]: { lineHeight: 1.4 },
} satisfies SxProps<Theme>;

/** `.ppc-solution-blur_item`: the green light over the dark panel's head. */
function SolutionLight() {
  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        inset: "-70% 0 auto",
        width: "600px",
        height: "600px",
        mx: "auto",
        borderRadius: "100vw",
        backgroundColor: "rgba(31, 190, 124, 0.7)",
        filter: "blur(600px)",
        [MID]: { height: "330px" },
      }}
    />
  );
}

/** `.ppc-problem_item` and `.ppc-solution_item`: the panel's cards, as many 18rem as fit. */
function PanelCards({
  items,
  tone: which,
}: {
  items: NonNullable<IssuePanel["items"]>;
  tone: "problem" | "solution";
}) {
  const tone = ISSUE_TONES[which];
  const dark = which === "solution";
  return (
    <Box
      sx={{
        position: "relative",
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 18rem), 1fr))",
        gap: "20px",
        width: 1,
      }}
    >
      {items.map((item) => (
        <Box
          key={item.title}
          sx={[
            {
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              p: "24px",
              borderRadius: "8px",
              [MID]: { p: "16px" },
              [TINY]: { gap: "8px" },
            },
            tone.card,
          ]}
        >
          {item.icon ? (
            <LandingImage
              src={item.icon}
              sx={{
                width: "48px",
                height: "48px",
                [MID]: { width: "32px", height: "32px" },
              }}
            />
          ) : null}
          <Box sx={SMALL_TITLE}>{item.title}</Box>
          {item.body ? (
            <Typography sx={dark ? DARK_CARD_BODY : BODY_SMALL}>
              {item.body}
            </Typography>
          ) : null}
        </Box>
      ))}
    </Box>
  );
}

/** The panel's own box: the dark one keeps 32 between its parts below the wide step. */
function panelBox(which: "problem" | "solution"): SxProps<Theme> {
  const dark = which === "solution";
  return [
    {
      position: "relative",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "48px",
      mb: "96px",
      p: "48px",
      borderRadius: "32px",
      [NOT_WIDE]: { mb: "64px", p: "32px", gap: dark ? "32px" : "48px" },
      [MID]: { p: "24px", borderRadius: "16px" },
      // Only the red panel closes its gap on a phone.
      [TINY]: {
        gap: dark ? "32px" : "24px",
        mb: "32px",
        p: dark ? "32px 16px" : "19.2px 11.2px",
      },
    },
    ISSUE_TONES[which].panel,
  ];
}

/**
 * A labelled panel of cards: `.ppx-problem_wrap` on its red wash, or
 * `.ppx-solution_wrap` dark under a green light, closing on its button.
 */
function Panel({
  panel: block,
  tone: which,
}: {
  panel: IssuePanel;
  tone: "problem" | "solution";
}) {
  const tone = ISSUE_TONES[which];
  const dark = which === "solution";
  return (
    <Box sx={panelBox(which)}>
      {dark ? <SolutionLight /> : null}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "12.8px",
          width: 1,
          maxWidth: "69ch",
          textAlign: "center",
        }}
      >
        {block.label ? (
          <Box
            sx={{
              color: tone.label,
              fontSize: 12.96,
              fontWeight: 500,
              textTransform: "uppercase",
            }}
          >
            {block.label}
          </Box>
        ) : null}
        <Typography
          component="h3"
          sx={[HEADING_2_ON_H3, { color: tone.ink, textAlign: "center" }]}
        >
          <Lines text={block.title} />
        </Typography>
        {block.body ? (
          <Typography
            sx={[BODY, dark ? { color: "common.white", opacity: 0.6 } : {}]}
          >
            {block.body}
          </Typography>
        ) : null}
      </Box>
      {block.items?.length ? (
        <PanelCards items={block.items} tone={which} />
      ) : null}
      <Closer ctas={block.ctas} notes={block.reassurances} onDark={dark} />
    </Box>
  );
}

/**
 * The problem and solution panels, in order. Where the page publishes them in
 * `.c-cloneable` they stack as it scrolls, each held 32 under the one before
 * above the wide step, the list inset past the section's gutter; alone, the
 * one panel is the closing band.
 */
export function IssuePanelsBlock({ block }: { block: IssuePanels }) {
  return (
    <LandingSection padding={block.pad ? sectionPad(block.pad) : "none"}>
      <Box sx={block.stacked ? { px: "20px", [TINY]: { px: 0 } } : undefined}>
        {block.panels.map((panel, index) => (
          <Box
            key={panel.title}
            sx={
              block.stacked
                ? {
                    [WIDE]: { position: "sticky", top: `${96 + 32 * index}px` },
                  }
                : undefined
            }
          >
            <Panel
              panel={panel}
              tone={block.issueTones?.[index] ?? "problem"}
            />
          </Box>
        ))}
      </Box>
    </LandingSection>
  );
}

type Gallery = Laid<"tileGallery">;

/**
 * The picture tiles for the use cases under a centred heading, three across,
 * and the button under them. On most pages `.use_case-cards_list` inside
 * `.why-web_item`, a CMS list whose tiles stand still; where the page copies
 * the product pages' `.use-case-pr_wrap` instead, the heading runs 70% wide,
 * each card carries its category and arrow and answers the pointer, and the
 * line under the button is the quieter holder.
 */
export function TileGalleryBlock({ block }: { block: Gallery }) {
  const copied = Boolean(block.productList);
  return (
    <LandingSection padding={block.pad ? sectionPad(block.pad) : "break"}>
      <Box
        sx={[
          {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "32px",
          },
          copied
            ? { [TINY]: { gap: "16px", alignItems: "stretch" } }
            : { [MID]: { gap: "24px" } },
        ]}
      >
        <Box
          sx={[
            {
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
              textAlign: "center",
            },
            copied
              ? { width: "70%", [NOT_WIDE]: { width: 1 } }
              : { width: 1, maxWidth: "592px" },
          ]}
        >
          <Typography component="h2" sx={HEADING_2}>
            <Lines text={block.title} />
          </Typography>
          {block.body ? (
            <Typography sx={BODY_SMALL}>{block.body}</Typography>
          ) : null}
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: "16px",
            width: 1,
            mt: copied ? 0 : "8px",
            [NOT_WIDE]: { gridTemplateColumns: "1fr 1fr" },
            [MID]: { gridTemplateColumns: "1fr" },
          }}
        >
          {block.items.map((tile) => (
            <UseCaseCard
              key={tile.title}
              still={!copied}
              card={{ ...tile, path: tile.href ?? "#", image: tile.picture }}
            />
          ))}
        </Box>
        <Closer ctas={block.ctas} notes={block.reassurances} quiet={copied} />
      </Box>
    </LandingSection>
  );
}

type CtaBand = Laid<"ctaBand">;

/**
 * `.ppc-cta_wrap`: the paid-search pages' closing call, its title set at hero
 * size as the page's second `h1`, which is how it is published.
 */
export function PpcCtaBand({ block }: { block: CtaBand }) {
  return (
    <LandingSection padding={block.pad ? sectionPad(block.pad) : "both"}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "40px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "24px",
            maxWidth: "670px",
            textAlign: "center",
          }}
        >
          {block.title ? (
            <Typography
              variant="h1"
              component="h1"
              sx={{ color: marketingPalette.ink }}
            >
              <Lines text={block.title} />
            </Typography>
          ) : null}
          {block.body ? <Typography sx={BODY}>{block.body}</Typography> : null}
        </Box>
        <Closer ctas={block.ctas} notes={block.reassurances} quiet />
      </Box>
    </LandingSection>
  );
}
